import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore, getAccessToken, getRefreshToken } from '@stores/authStore'

// API Base URL — when VITE_API_URL is not set, use same-origin (nginx proxies /api/ to the API).
// This avoids CORS issues entirely on staging where cross-origin calls can fail during deploys.
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // FormData uploads must NOT use the instance default `Content-Type: application/json`.
    // Removing the header lets the browser set `multipart/form-data; boundary=…` itself,
    // so FastAPI can parse `file`/fields. Otherwise every FormData upload (documents, STT,
    // property assets, …) fails with 422 — regardless of the per-call config object.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Token refresh queue — prevents multiple concurrent refresh attempts
let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) return refreshPromise

  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new Error('No refresh token')

  refreshPromise = axios
    .post(`${API_BASE_URL}/api/auth/refresh`, { refresh_token: refreshToken })
    .then(response => {
      const { access_token, refresh_token } = response.data
      useAuthStore.getState().setTokens(access_token, refresh_token)
      return access_token as string
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't intercept auth endpoints — let callers handle errors directly
      const url = originalRequest.url || ''
      const isAuthEndpoint = url.includes('/auth/magic-link/') || url.includes('/auth/login') || url.includes('/auth/verify-code')
      if (isAuthEndpoint) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const newToken = await refreshAccessToken()
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch {
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

export default api

/**
 * Resolve an image URL to an absolute URL.
 * Handles both old absolute URLs (https://api.../media/X) and
 * newer relative URLs (/api/documents/public/media/X).
 */
export function resolveImageUrl(url: string | null | undefined): string {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return `${API_BASE_URL}${url}`
}

// =============================================================================
// Auth API
// =============================================================================

export interface LoginRequest {
  email: string
  password: string
}

// STORY-014: Company role info from login
export interface UserCompanyRole {
  id: number
  company_id: number
  company_name: string
  company_uid: string
  function: string | null
  signature_type: string | null
  is_internal: boolean
}

export interface LoginContractInfo {
  id: number
  contract_number: string
  contract_type: string
  status: string
  signed_at: string | null
  expires_at: string | null
  is_active: boolean
  roles: string[] | null
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: {
    id: number
    email: string
    first_name: string | null
    last_name: string | null
    role: 'admin' | 'sales' | 'investor' | 'signer'
    is_active: boolean
    created_at: string
    updated_at: string | null
    // STORY-1033: Server-side active Mandant (users.active_company_id)
    active_company_id?: number | null
    // STORY-1145: false for accounts without a password (magic link, signing)
    has_password?: boolean
  }
  // STORY-014: Companies the user can act on behalf of
  companies?: UserCompanyRole[]
  // STORY-132: Contracts for permission checks
  contracts?: LoginContractInfo[]
  // STORY-1023: Target page carried by a magic link (relative in-app path)
  next_url?: string | null
}

// STORY-173: 2FA Email Code — matches backend VerificationRequiredResponse
export interface TwoFactorRequiredResponse {
  requires_verification: true
  session_id: string
  email_hint: string
  message: string
}

export type LoginApiResponse = LoginResponse | TwoFactorRequiredResponse

export function isTwoFactorRequired(res: LoginApiResponse): res is TwoFactorRequiredResponse {
  return 'requires_verification' in res && res.requires_verification === true
}

/** Response of PUT /api/auth/me/active-company (users.active_company_id) */
export interface ActiveCompanyResponse {
  active_company_id: number | null
}

export interface RegisterRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  role?: 'admin' | 'sales' | 'investor'
}

export const authApi = {
  login: (data: LoginRequest) =>
    api.post<LoginApiResponse>('/api/auth/login', data),

  // STORY-173: 2FA verification
  verify2FA: (data: { session_id: string; code: string }) =>
    api.post<LoginResponse>('/api/auth/verify-code', data),

  resend2FA: (data: { session_id: string }) =>
    api.post<TwoFactorRequiredResponse>('/api/auth/verify-code/resend', data),

  register: (data: RegisterRequest) => 
    api.post('/api/auth/register', data),

  me: () => 
    api.get('/api/auth/me'),

  refresh: (refreshToken: string) =>
    api.post('/api/auth/refresh', { refresh_token: refreshToken }),

  updateProfile: (data: {
    first_name: string;
    last_name: string;
    preferred_language?: string | null;
    function?: string | null;
    direct_phone?: string | null;
  }) => api.put('/api/auth/me', data),

  changePassword: (data: { current_password: string; new_password: string }) =>
    api.put('/api/auth/me/password', data),

  listMyCompanies: () =>
    api.get<Array<{
      id: number
      company_id: number
      company_name: string
      function: string | null
      signature_type: string
      signature_text: string | null
    }>>('/api/auth/me/companies'),

  /**
   * Persist the active Mandant server-side (STORY-1033).
   * Tenant-scoped views (Quality Gate, AI extraction, ...) resolve the tenant
   * from users.active_company_id — a purely local selection has no effect.
   * Pass null to clear the selection and fall back to the primary Mandant.
   */
  setActiveCompany: (companyId: number | null) =>
    api.put<ActiveCompanyResponse>('/api/auth/me/active-company', { company_id: companyId }),

  updateMyCompany: (ucId: number, data: {
    function?: string | null
    signature_type?: string | null
    signature_text?: string | null
  }) => api.patch(`/api/auth/me/companies/${ucId}`, data),

  forgotPassword: (email: string) =>
    api.post('/api/auth/forgot-password', { email }),

  validatePasswordToken: (token: string) =>
    api.get<{ valid: boolean; email: string; first_name: string | null; last_name: string | null }>(
      `/api/auth/password-setup/validate/${token}`
    ),

  completePasswordSetup: (data: { token: string; new_password: string }) =>
    api.post<{ message: string }>('/api/auth/password-setup/complete', data),

  requestMagicLink: (email: string) =>
    api.post<{ message: string }>('/api/auth/magic-link/request', { email }),

  /**
   * STORY-1038: Neuen Anmelde-Link nachfordern, ohne die E-Mail-Adresse erneut
   * einzugeben. Das Kennzeichen stammt aus dem Fehlerdetail eines abgelaufenen
   * `magic-link/verify`; der Server ermittelt daraus den Empfaenger. Die
   * Antwort ist immer neutral (keine Konto-Enumeration), auch wenn das
   * Nachforder-Limit erreicht ist.
   */
  renewMagicLink: (renewToken: string) =>
    api.post<{ message: string }>('/api/auth/magic-link/request', { renew_token: renewToken }),

  verifyMagicLink: (token: string) =>
    api.post<LoginResponse>('/api/auth/magic-link/verify', { token }),

  // Passkey management
  listPasskeys: () =>
    api.get<import('@/types/investor').Passkey[]>('/api/auth/passkeys'),

  getPasskeyRegistrationOptions: () =>
    api.post<import('@/types/investor').PasskeyRegistrationOptions>('/api/auth/passkey/register/begin'),

  registerPasskey: (data: { credential: unknown; challenge: string; device_name?: string }) =>
    api.post<import('@/types/investor').PasskeyRegistrationResponse>('/api/auth/passkey/register/complete', data),

  updatePasskey: (id: number, data: { device_name: string }) =>
    api.patch<import('@/types/investor').Passkey>(`/api/auth/passkeys/${id}`, data),

  deletePasskey: (id: number) =>
    api.delete(`/api/auth/passkeys/${id}`),

  // API Token management
  listApiTokens: () =>
    api.get<ApiToken[]>('/api/auth/tokens'),

  createApiToken: (data: { name: string; expires_at?: string | null }) =>
    api.post<ApiTokenCreated>('/api/auth/tokens', data),

  revokeApiToken: (id: number) =>
    api.delete(`/api/auth/tokens/${id}`),
}

export interface ApiToken {
  id: number
  name: string
  prefix: string
  last_used_at: string | null
  expires_at: string | null
  is_active: boolean
  created_at: string
}

export interface ApiTokenCreated extends ApiToken {
  token: string // Full token — shown only once
}

// =============================================================================
// Developer API (#1240) — Monatsnutzung der API-Werkzeuge
// =============================================================================

/** Eine Zeile der Monatsübersicht: ein Werkzeug in einem Monat. */
export interface DeveloperUsageRow {
  month: string
  tool: string
  calls: number
  units: number
  chf: number
}

export interface DeveloperUsageResponse {
  from: string
  to: string
  rows: DeveloperUsageRow[]
  total_calls: number
  total_units: number
  total_chf: number
}

export interface DeveloperUsageParams {
  from?: string
  to?: string
  /** Nur admin/tenant_admin — für die Rolle developer ignoriert der Server den Wert. */
  company_id?: number
}

// -----------------------------------------------------------------------------
// Prepaid-Guthaben (#1241) — Ledger, Aufladen per Stripe Checkout, Quittung
// -----------------------------------------------------------------------------

export type DeveloperCreditKind =
  | 'topup_card'
  | 'topup_invoice'
  | 'charge'
  | 'correction'
  | 'voucher'

/** Eine Buchung des Ledgers. Beträge kommen als Pydantic-Decimal, also als String. */
export interface DeveloperCreditBooking {
  id: number
  created_at: string
  kind: DeveloperCreditKind
  amount_chf: number | string
  balance_after: number | string
  reference: string | null
  note: string | null
}

export interface DeveloperCreditsResponse {
  company_id: number | null
  balance_chf: number | string
  currency: string
  low_threshold_chf: number | string
  /** true, wenn dem Zugang keine Firma zugeordnet ist — dann kein Aufladen. */
  no_company: boolean
  bookings: DeveloperCreditBooking[]
}

export interface DeveloperCreditsParams {
  /** Nur admin/tenant_admin — für die Rolle developer ignoriert der Server den Wert. */
  company_id?: number
  limit?: number
}

export interface DeveloperTopupRequest {
  /** Betrag in CHF, 50–5000. */
  amount_chf: number
}

export interface DeveloperTopupResponse {
  checkout_url: string
  session_id: string
}

/**
 * Buchung durch Immotron (#1242): Aufladung auf Rechnung oder Korrektur.
 * `amount_chf` mit zwei Nachkommastellen — bei `topup_invoice` > 0, bei
 * `correction` ≠ 0.
 */
export interface DeveloperCreditBookingCreate {
  company_id: number
  kind: 'topup_invoice' | 'correction'
  amount_chf: number
  reference?: string
  note?: string
}

/** Gutscheincode einlösen (#1259) — die Prüfung des Formats macht der Server. */
export interface VoucherRedeemRequest {
  code: string
}

/**
 * Antwort des Einlösens: gutgeschriebener Betrag und neuer Saldo. Beträge
 * kommen als Pydantic-Decimal, also als String («30.00»).
 */
export interface VoucherRedeemResponse {
  code: string
  amount_chf: number | string
  balance_chf: number | string
}

/** Ein Gutscheincode in der Admin-Verwaltung (#1259). */
export interface VoucherCode {
  id: number
  code: string
  amount_chf: number | string
  max_redemptions: number
  /** Bisherige Einlösungen — der Vertrag nennt das Feld `redemptions`. */
  redemptions: number
  once_per_company: boolean
  valid_until: string | null
  note: string | null
  is_active: boolean
  created_at: string
}

export interface VoucherListParams {
  /** Ohne Angabe liefert der Server alle Codes, auch deaktivierte. */
  is_active?: boolean
  /** Suche über Code und Notiz. */
  q?: string
}

/** Mehrere Codes auf einen Schlag erzeugen — `count` zwischen 1 und 100. */
export interface VoucherCreateRequest {
  count: number
  amount_chf: number
  max_redemptions: number
  once_per_company: boolean
  /** ISO-Datum (YYYY-MM-DD) oder weggelassen für «unbegrenzt». */
  valid_until?: string
  note?: string
}

/**
 * Selbstregistrierung für den Entwickler-Zugang (#1243) — öffentlich, ohne
 * Authorization-Header. `language` steuert die Sprache der Bestätigungsmail.
 */
export interface DeveloperSignupRequest {
  company_name: string
  first_name: string
  last_name: string
  email: string
  accept_terms: boolean
  language: 'de' | 'en' | 'fr' | 'it'
}

export const developerApi = {
  /**
   * Antwortet immer 202 — auch bei bereits bekannter Adresse (keine
   * Auskunft darüber, welche Adressen registriert sind).
   */
  signup: (data: DeveloperSignupRequest) =>
    api.post<{ message: string }>('/api/developer/signup', data),

  getUsage: (params: DeveloperUsageParams) =>
    api.get<DeveloperUsageResponse>('/api/developer/usage', { params }),

  /**
   * CSV über die Axios-Instanz laden, damit der Bearer-Header gesetzt bleibt
   * (ein einfacher Link auf die URL liefert sonst 401).
   */
  getUsageCsv: (params: DeveloperUsageParams) =>
    api.get<Blob>('/api/developer/usage', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    }),

  /** Saldo und Buchungen des Prepaid-Guthabens (#1241). */
  getCredits: (params?: DeveloperCreditsParams) =>
    api.get<DeveloperCreditsResponse>('/api/developer/credits', { params }),

  /** Stripe-Checkout anlegen — die Antwort enthält die Weiterleitungs-Adresse. */
  createTopup: (data: DeveloperTopupRequest) =>
    api.post<DeveloperTopupResponse>('/api/developer/credits/topups', data),

  /**
   * Guthaben einer Kundenfirma buchen (#1242) — nur admin/tenant_admin.
   * Antwortet 200 statt 201, wenn `(kind, reference)` schon gebucht war.
   */
  createBooking: (data: DeveloperCreditBookingCreate) =>
    api.post<DeveloperCreditBooking>('/api/developer/credits/bookings', data),

  /**
   * Gutscheincode einlösen (#1259) — gutgeschrieben wird immer der eigenen
   * Firma. Fehler kommen als `detail.code`: `VOUCHER_INVALID`,
   * `VOUCHER_EXHAUSTED` oder `VOUCHER_ALREADY_REDEEMED`.
   */
  redeemVoucher: (data: VoucherRedeemRequest) =>
    api.post<VoucherRedeemResponse>('/api/developer/credits/vouchers/redeem', data),

  /**
   * Quittung einer Aufladung als PDF — wie der CSV-Export über die
   * Axios-Instanz, damit der Bearer-Header gesetzt bleibt.
   */
  getReceiptPdf: (bookingId: number) =>
    api.get<Blob>(`/api/developer/credits/bookings/${bookingId}/receipt`, {
      responseType: 'blob',
    }),

  // --- Verwaltung der Gutscheincodes (#1259) — nur admin/tenant_admin ---

  /** Der Endpunkt antwortet mit einem flachen Array, nicht mit `{items,total}`. */
  listVouchers: (params?: VoucherListParams) =>
    api.get<VoucherCode[]>('/api/admin/vouchers', { params }),

  /** Erzeugt `count` Codes mit gleichem Betrag und gleicher Gültigkeit. */
  createVouchers: (data: VoucherCreateRequest) =>
    api.post<VoucherCode[]>('/api/admin/vouchers', data),

  /** Schaltet einen Code aus — mit `isActive: true` wieder ein. */
  deactivateVoucher: (voucherId: number, isActive: boolean) =>
    api.patch<VoucherCode>(`/api/admin/vouchers/${voucherId}`, { is_active: isActive }),
}

// =============================================================================
// Users API (Admin)
// =============================================================================

export interface User {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  role: 'admin' | 'sales' | 'investor' | 'developer'
  is_active: boolean
  created_at: string
  updated_at: string | null
  contact_id: number | null
  last_login: string | null
  preferred_language: string | null
  function: string | null
  direct_phone: string | null
}

export interface CreateUserRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  role: 'admin' | 'sales' | 'investor' | 'developer'
}

export interface UpdateUserRequest {
  first_name?: string
  last_name?: string
  role?: 'admin' | 'sales' | 'investor' | 'developer'
  is_active?: boolean
  contact_id?: number | null
  preferred_language?: string | null
  function?: string | null
  direct_phone?: string | null
}

export const usersApi = {
  list: (params?: { page?: number; page_size?: number; role?: string; search?: string; is_active?: boolean; is_internal?: boolean }) =>
    api.get<import('@/types/pagination').PaginatedResponse<User>>('/api/admin/users', { params }),

  get: (id: number) =>
    api.get<User>(`/api/admin/users/${id}`),

  create: (data: CreateUserRequest) =>
    api.post<User>('/api/admin/users', data),

  update: (id: number, data: UpdateUserRequest) =>
    api.put<User>(`/api/admin/users/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/admin/users/${id}`),

  sendReset: (id: number) =>
    api.post<{ message: string; reset_url?: string }>(`/api/admin/users/${id}/send-reset`),

  impersonate: (id: number) =>
    api.post<LoginResponse>(`/api/admin/users/${id}/impersonate`),
}

// =============================================================================
// Schema API (Admin - ERD Visualization)
// =============================================================================

export interface ColumnInfo {
  name: string
  type: string
  nullable: boolean
  primary_key: boolean
  foreign_key: string | null
}

export interface TableInfo {
  name: string
  columns: ColumnInfo[]
}

export interface ForeignKeyInfo {
  source_table: string
  source_column: string
  target_table: string
  target_column: string
}

export interface SchemaResponse {
  tables: TableInfo[]
  relationships: ForeignKeyInfo[]
}

export const schemaApi = {
  get: () => api.get<SchemaResponse>('/api/admin/schema'),
}

// =============================================================================
// Properties API
// =============================================================================

export interface Property {
  id: number
  title: string
  description: string | null
  property_type: string
  status: string
  street: string | null
  city: string | null
  postal_code: string | null
  country: string
  price: number | null
  currency: string
  area_sqm: number | null
  rooms: number | null
  owner_id: number
  created_at: string
  updated_at: string | null
}

export interface PropertyListItem {
  id: number
  property_type: string
  city: string | null
  canton: string | null
  /** Kanonischer Preis (#360) — die Liste liefert `price`, nicht `minimum_price`. */
  price?: number | null
  /** @deprecated Kanonisch ist `price` (#360). */
  minimum_price: number | null
  annual_rental_income: number | null
  lot_size_sqm: number | null
  usable_area_sqm: number | null
  status: string
  crm_company_id: number | null
  tenant_id: number | null
  is_private_listing: boolean
  created_at: string
  name: string | null
  primary_image_url: string | null
  latitude: number | null
  longitude: number | null
  street_address: string | null
  // Sales-process state — `EXTRACTION_FAILED` flags AI-extraction quarantine (#616).
  property_state?: import('@/types/property').PropertyState | null
  favorite_count?: number
  owner_id?: number | null
  owner_name?: string | null
  /** Gesetzt bei Filterung via `crm_company_id`: über welchen Kontakt der
   *  Firma das Objekt zugeordnet ist (#1205). `null` = direkt der Firma. */
  via_contact?: { id: number; name: string } | null
}

/**
 * Auftrag an den Objekt-Agenten (#1244, API «Analyse-Auftrag»).
 *
 * `status` ist der Zustand des Hintergrund-Jobs, `property_status` der daraus
 * abgeleitete Objektstatus (`ANALYSIS_REQUESTED` / `ANALYSIS_DONE`).
 */
export interface AgentAnalysisJob {
  job_id: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  /** 0–100. */
  progress: number
  progress_message: string | null
  property_id: number
  property_status: string
  depth: string
  result: {
    summary?: string
    open_points?: string[]
    /** Mayan-Dokument des Prüfberichts — PDF via `/api/documents/{id}/download`. */
    report_document_id?: number
  } | null
  created_at: string
  updated_at: string
}

/** Analyse-Tiefe A (Standard) bis C; `note` max. 2000 Zeichen. */
export interface AgentAnalysisRequest {
  depth: 'A' | 'B' | 'C'
  note?: string
}

export interface PropertyFavoriteUser {
  user_id: number
  email: string
  name: string
  role: string
  favorited_at: string | null
}

export const propertiesApi = {
  // Sales endpoints - extended filters, search, sorting
  list: (params?: {
    page?: number
    page_size?: number
    status?: string
    company_ids?: number[]
    /** CRM-Firma: Objekte der Firma UND ihrer aktiven Kontakte (#1205).
     *  Nicht zu verwechseln mit `company_ids` (Mandanten-Filter). */
    crm_company_id?: number
    search?: string
    property_type?: string
    canton?: string
    min_price?: number
    max_price?: number
    sort_by?: string
    lang?: string
  }) => {
    const { company_ids, canton, ...rest } = params || {}
    const queryParams: Record<string, unknown> = { ...rest }
    if (company_ids && company_ids.length > 0) {
      queryParams.company_ids = company_ids.join(',')
    }
    if (canton) {
      queryParams.canton = canton
    }
    return api.get<import('@/types/pagination').PaginatedResponse<PropertyListItem>>(
      '/api/sales/properties',
      { params: queryParams }
    )
  },

  get: (id: number, lang?: string) =>
    api.get<import('@/types/property').PropertyDetail>(
      `/api/sales/properties/${id}`,
      { params: lang ? { lang } : undefined }
    ),

  getPrincipal: (id: number) =>
    api.get<import('@/types/property').PrincipalHeaderResponse>(
      `/api/sales/properties/${id}/principal`
    ),

  /** Auftraggeber-Suche (#398, #407). Backend filtert ab #407 auf Firmen/
   *  Kontakte mit aktivem Plattformvertrag (SELLER_FRAMEWORK, SIGNED). */
  searchPrincipal: (id: number, q: string, limit = 20) =>
    api.get<import('@/types/property').PrincipalSearchResult[]>(
      `/api/sales/properties/${id}/principal/search`,
      { params: { q, limit } }
    ),

  updatePrincipal: (id: number, data: import('@/types/property').PrincipalUpdateRequest) =>
    api.put<import('@/types/property').PrincipalHeaderResponse>(
      `/api/sales/properties/${id}/principal`,
      data
    ),

  getFavorites: (id: number) =>
    api.get<PropertyFavoriteUser[]>(`/api/sales/properties/${id}/favorites`),

  create: (data: import('@/types/property').PropertyCreateData) =>
    api.post<import('@/types/property').PropertyDetail>('/api/sales/properties', data),

  update: (id: number, data: import('@/types/property').PropertyUpdateData, lang?: string) =>
    api.put<import('@/types/property').PropertyDetail>(
      `/api/sales/properties/${id}`,
      data,
      { params: lang ? { lang } : undefined }
    ),

  delete: (id: number) =>
    api.delete(`/api/sales/properties/${id}`),

  // Objekt-Analyse durch den Objekt-Agenten (#1244).
  /** Startet die Vollanalyse. 409 `ANALYSIS_RUNNING`, wenn schon eine läuft. */
  startAgentAnalysis: (id: number, data: AgentAnalysisRequest) =>
    api.post<AgentAnalysisJob>(`/api/sales/properties/${id}/agent-analysis`, data),

  /** Jüngster Analyse-Auftrag des Objekts. 404 `NO_ANALYSIS`, wenn keiner existiert. */
  getAgentAnalysis: (id: number) =>
    api.get<AgentAnalysisJob>(`/api/sales/properties/${id}/agent-analysis`),

  // Translations
  upsertTranslation: (propertyId: number, langCode: string, data: import('@/types/property').PropertyTranslationInput) =>
    api.put<import('@/types/property').PropertyTranslation>(
      `/api/sales/properties/${propertyId}/translations/${langCode}`,
      data
    ),

  deleteTranslation: (propertyId: number, langCode: string) =>
    api.delete(`/api/sales/properties/${propertyId}/translations/${langCode}`),

  // Images
  addImage: (propertyId: number, data: import('@/types/property').PropertyImageCreate) =>
    api.post<import('@/types/property').PropertyImage>(
      `/api/sales/properties/${propertyId}/images`,
      data
    ),

  /** List images for a property. Pass ``includeDeleted=true`` to also receive
   *  soft-deleted images (Archiv-Sicht im Media-Tab, Issue #791). */
  listImages: (propertyId: number, includeDeleted = false) =>
    api.get<import('@/types/property').PropertyImage[]>(
      `/api/sales/properties/${propertyId}/images`,
      { params: includeDeleted ? { include_deleted: true } : undefined }
    ),

  deleteImage: (propertyId: number, imageId: number) =>
    api.delete(`/api/sales/properties/${propertyId}/images/${imageId}`),

  /** Restore a previously soft-deleted image (Issue #791 / API #622). */
  restoreImage: (propertyId: number, imageId: number) =>
    api.post<import('@/types/property').PropertyImage>(
      `/api/sales/properties/${propertyId}/images/${imageId}/restore`
    ),

  updateImage: (propertyId: number, imageId: number, data: { is_primary?: boolean; sort_order?: number; alt_text?: string; show_on_teaser?: boolean; location_risk?: string | null }) =>
    api.patch<import('@/types/property').PropertyImage>(
      `/api/sales/properties/${propertyId}/images/${imageId}`,
      data
    ),

  rotateImage: (propertyId: number, imageId: number, degrees: number) =>
    api.patch<import('@/types/property').PropertyImage>(
      `/api/sales/properties/${propertyId}/images/${imageId}/rotate`,
      { degrees }
    ),

  uploadImageFile: async (propertyId: number, file: File, sortOrder: number, isPrimary: boolean) => {
    // Step 1: Upload file to Mayan EDMS via property-assets endpoint
    const formData = new FormData()
    formData.append('file', file)
    const uploadRes = await api.post<{ mayan_document_id: number }>(
      '/api/documents/property-assets',
      formData,
      { timeout: 60000 }
    )
    const mayanId = uploadRes.data.mayan_document_id

    // Step 2: Register the public URL as a property image
    // Use full URL so images work from any origin (frontend ≠ API domain)
    const publicUrl = `${API_BASE_URL}/api/documents/public/media/${mayanId}`
    const imageData: import('@/types/property').PropertyImageCreate = {
      image_url: publicUrl,
      is_primary: isPrimary,
      sort_order: sortOrder,
    }
    const res = await propertiesApi.addImage(propertyId, imageData)
    return res
  },

  // Investor Access
  grantAccess: (propertyId: number, investorId: number) =>
    api.post<import('@/types/property').PropertyInvestorAccess>(
      `/api/sales/properties/${propertyId}/access`,
      { investor_id: investorId }
    ),

  revokeAccess: (propertyId: number, investorId: number) =>
    api.delete(`/api/sales/properties/${propertyId}/access/${investorId}`),

  getAccessList: (propertyId: number, params?: { page?: number; page_size?: number }) =>
    api.get<import('@/types/pagination').PaginatedResponse<import('@/types/property').PropertyInvestorAccess>>(
      `/api/sales/properties/${propertyId}/access`,
      { params }
    ),

  getActivity: (propertyId: number, params?: { page?: number; page_size?: number }) =>
    api.get<import('@/types/pagination').PaginatedResponse<import('@/types/property').NotificationActivity>>(
      `/api/sales/properties/${propertyId}/activity`,
      { params }
    ),

  // Group Access
  grantGroupAccess: (propertyId: number, groupId: number) =>
    api.post<import('@/types/property').PropertyGroupAccess>(
      `/api/sales/properties/${propertyId}/group-access`,
      { group_id: groupId }
    ),

  revokeGroupAccess: (propertyId: number, groupId: number) =>
    api.delete(`/api/sales/properties/${propertyId}/group-access/${groupId}`),

  getGroupAccessList: (propertyId: number) =>
    api.get<import('@/types/property').PropertyGroupAccess[]>(
      `/api/sales/properties/${propertyId}/group-access`
    ),

  // Knowledge Chunks (Issue #586/#767): an Immobilie geheftete Wissensbausteine.
  getChunks: (propertyId: number) =>
    api.get<import('@/types/task').EntityChunkListResponse>(
      `/api/sales/properties/${propertyId}/chunks`,
    ),

  // KI-Texte neu generieren (Issue #838 / API #646): Subheader, Highlights,
  // Beschreibung sowie Makro-/Mikrolage. Backend schreibt in die DE-Übersetzung.
  // Mit ``field`` wird NUR dieses eine Feld neu generiert (API #793 / Web #944);
  // ``instruction`` webt eine freie Bemerkung in den Generierungs-Prompt ein.
  regenerateTexts: (
    propertyId: number,
    opts: import('@/types/property').PropertyTextsRequest = {},
  ) =>
    api.post<import('@/types/property').PropertyTextsResponse>(
      `/api/sales/properties/${propertyId}/generate-texts`,
      undefined,
      {
        params: {
          overwrite: opts.overwrite ?? true,
          ...(opts.field ? { field: opts.field } : {}),
          ...(opts.instruction ? { instruction: opts.instruction } : {}),
        },
      },
    ),

  // Quality Gate
  submitForReview: (propertyId: number) =>
    api.post(`/api/sales/properties/${propertyId}/submit-review`),

  approveProperty: (propertyId: number) =>
    api.post(`/api/sales/properties/${propertyId}/approve`),

  rejectProperty: (propertyId: number, reason: string) =>
    api.post(`/api/sales/properties/${propertyId}/reject`, { reason }),

  activateProperty: (propertyId: number) =>
    api.post(`/api/sales/properties/${propertyId}/activate`),

  // Datenraum — Zugriffsprotokoll & Leck-Zuordnung (Web #1100)

  /**
   * Dokument-Zugriffsprotokoll eines Objekts (API #1024). Neueste Zeile
   * zuerst; ``user_id`` filtert auf die Zugriffe eines einzelnen Käufers.
   * Nur Sales/Admin.
   */
  getDocumentAccessLog: (
    propertyId: number,
    params?: { user_id?: number; page?: number; page_size?: number },
  ) =>
    api.get<import('@/types/pagination').PaginatedResponse<import('@/types/property').DocumentAccessLogEntry>>(
      `/api/sales/properties/${propertyId}/document-access-log`,
      { params }
    ),

  /**
   * Geleaktes Dokument seiner Auslieferung zuordnen (API #1025) — nur Admin.
   * Liest den unsichtbaren Fingerabdruck aus Bild bzw. PDF und löst ihn über
   * das Zugriffsprotokoll auf. Ohne lesbare Kennung kommt ``found: false``.
   */
  identifyDocumentFingerprint: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<import('@/types/property').DocumentFingerprintIdentifyResult>(
      '/api/sales/document-fingerprint/identify',
      formData,
      { timeout: 120000 }
    )
  },

  // Unit Mix (Wohnungsmix)
  getUnitMix: (propertyId: number) =>
    api.get<import('@/types/property').UnitMixResponse[]>(`/api/sales/properties/${propertyId}/unit-mix`),

  replaceUnitMix: (propertyId: number, items: import('@/types/property').UnitMixInput[]) =>
    api.put<import('@/types/property').UnitMixResponse[]>(`/api/sales/properties/${propertyId}/unit-mix`, items),

  // Rent Roll (Mieterspiegel)
  getRentRoll: (propertyId: number) =>
    api.get<import('@/types/property').RentRollResponse[]>(`/api/sales/properties/${propertyId}/rent-roll`),

  replaceRentRoll: (propertyId: number, items: import('@/types/property').RentRollInput[]) =>
    api.put<import('@/types/property').RentRollResponse[]>(`/api/sales/properties/${propertyId}/rent-roll`, items),
}

// =============================================================================
// Investor Groups API
// =============================================================================

export const investorGroupsApi = {
  list: (params?: { page?: number; page_size?: number; search?: string }) =>
    api.get<import('@/types/pagination').PaginatedResponse<import('@/types/property').InvestorGroup>>(
      '/api/admin/investor-groups',
      { params }
    ),

  get: (id: number) =>
    api.get<import('@/types/property').InvestorGroupDetail>(`/api/admin/investor-groups/${id}`),

  create: (data: { name: string; description?: string }) =>
    api.post<import('@/types/property').InvestorGroup>('/api/admin/investor-groups', data),

  update: (id: number, data: { name?: string; description?: string }) =>
    api.put<import('@/types/property').InvestorGroup>(`/api/admin/investor-groups/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/admin/investor-groups/${id}`),

  addMember: (groupId: number, investorId: number) =>
    api.post<import('@/types/property').InvestorGroupMember>(
      `/api/admin/investor-groups/${groupId}/members`,
      { investor_id: investorId }
    ),

  removeMember: (groupId: number, investorId: number) =>
    api.delete(`/api/admin/investor-groups/${groupId}/members/${investorId}`),

  addMembersBatch: (groupId: number, investorIds: number[]) =>
    api.post<{ added: number; skipped: number }>(
      `/api/admin/investor-groups/${groupId}/members/batch`,
      { investor_ids: investorIds }
    ),
}

// =============================================================================
// Contracts API
// =============================================================================

export interface ContractGenerateRequest {
  contract_type: string
  data: Record<string, unknown>
  language?: string
  output_format?: 'pdf' | 'docx' | 'html'
}

export const contractsApi = {
  generate: (data: ContractGenerateRequest) =>
    api.post('/api/contracts/generate', data, { responseType: 'blob' }),

  getTypes: () =>
    api.get<string[]>('/api/contracts/types'),

  getTemplate: (type: string, language?: string) =>
    api.get(`/api/sales/contracts/templates/${type}`, { params: { language } }),
}

// =============================================================================
// Settings API (Theme)
// =============================================================================

export interface AISettingsResponse {
  api_key_masked: string
  api_key_set: boolean
  enabled: boolean
  model: string
}

export interface AITestResponse {
  success: boolean
  model?: string
  duration_seconds?: number
  error?: string
}

export interface AIServiceBalance {
  name: string
  key_configured: boolean
  balance: number | null
  currency: string | null
  status: 'active' | 'error' | 'not_configured' | 'no_api'
  message: string
  dashboard_url: string
  usage_month_chf?: number
  usage_month_tokens?: number
  usage_month_calls?: number
}

export interface AIBalancesResponse {
  services: AIServiceBalance[]
  total_month_chf?: number
  month?: string
}

/**
 * Preise der Objektanalyse und des Entwickler-Zugangs (#1258). Die Beträge
 * kommen als Pydantic-Decimal, also als String — vor dem Rechnen `Number()`.
 * `analysis_sources` ist ein Zuschlag je lizenzierter Quelle, Schlüssel wie in
 * `GET /api/analyses/sources`.
 */
export interface PricingSettingsResponse {
  analysis_price_reduced: number | string
  analysis_price_full: number | string
  analysis_sources: Record<string, number | string>
  developer_price_property_evaluate: number | string
  developer_credit_low_chf: number | string
  currency: string
  updated_by: string | null
  updated_at: string | null
}

/** PUT-Rumpf — nur geänderte Felder senden. */
export interface PricingSettingsUpdate {
  analysis_price_reduced?: number
  analysis_price_full?: number
  analysis_sources?: Record<string, number>
  developer_price_property_evaluate?: number
  developer_credit_low_chf?: number
}

export const settingsApi = {
  getTheme: () =>
    api.get<{ preset: string }>('/api/settings/theme'),

  updateTheme: (preset: string) =>
    api.put<{ preset: string }>('/api/admin/settings/theme', { preset }),

  getAISettings: () =>
    api.get<AISettingsResponse>('/api/admin/settings/ai'),

  updateAISettings: (data: { api_key?: string; enabled?: boolean; model?: string }) =>
    api.put<{ api_key_masked: string; api_key_set: boolean; message: string }>('/api/admin/settings/ai', data),

  testAIConnection: () =>
    api.post<AITestResponse>('/api/admin/settings/ai/test'),

  getAIBalances: () =>
    api.get<AIBalancesResponse>('/api/admin/settings/ai/balances'),

  /** Preise Objektanalyse / Entwickler-Zugang (#1258) — nur Rolle admin. */
  getPricing: () =>
    api.get<PricingSettingsResponse>('/api/admin/settings/pricing'),

  updatePricing: (data: PricingSettingsUpdate) =>
    api.put<PricingSettingsResponse>('/api/admin/settings/pricing', data),
}

// =============================================================================
// Notifications API (Bell)
// =============================================================================

export interface NotificationItem {
  id: number
  notification_type: string
  recipient_email: string
  recipient_name: string | null
  property_id: number | null
  listing_id: number | null
  triggered_by_id: number | null
  sent_at: string | null
  read_at: string | null
  error_message: string | null
  created_at: string
  // Enriched fields
  triggered_by_name: string | null
  property_title: string | null
  summary: string | null
}

export interface NotificationListResponse {
  unread_count: number
  items: NotificationItem[]
  total: number
  page: number
  page_size: number
}

export const notificationsApi = {
  list: (params?: { page?: number; page_size?: number }) =>
    api.get<NotificationListResponse>('/api/notifications', { params }),

  markAsRead: (id: number) =>
    api.patch<{ id: number; read_at: string }>(`/api/notifications/${id}/read`),

  markAllAsRead: () =>
    api.post<{ marked_count: number }>('/api/notifications/read-all'),

  resend: (id: number) =>
    api.post<{ message: string; id: number; sent_at: string }>(`/api/notifications/${id}/resend`),
}

// =============================================================================
// Objektanalyse — Käufer-Modul (API #1184/#1185)
// =============================================================================

/** Gruppen der Quellenliste — bestimmt die Überschriften im Formular. */
export type AnalysisSourceGroup = 'amtlich' | 'register' | 'lizenziert'

export type AnalysisDepth = 'reduced' | 'full'

export type AnalysisStatus = 'pending' | 'running' | 'done' | 'failed' | 'refunded'

/** Eine wählbare Quelle. Preise kommen als Pydantic-Decimal, also als String. */
export interface AnalysisSource {
  key: string
  group: AnalysisSourceGroup
  label: string
  price_chf: string
  /** true = im Grundpreis enthalten, zählt nicht zusätzlich. */
  included_in_base: boolean
}

export interface AnalysisSourcesResponse {
  sources: AnalysisSource[]
  base_prices: { reduced: string; full: string }
  currency: string
}

/**
 * Mit welchem Suchprofil die Analyse abgeglichen wird. `null` oder weggelassen
 * = der Server entscheidet (eigenes Profil, sonst Firmenprofil).
 */
export type AnalysisProfileScope = 'contact' | 'company'

export interface AnalysisCreate {
  /** Objekttitel, 3–255 Zeichen (api#1189). */
  title: string
  street_address?: string
  zip_code?: string
  city?: string
  egrid_number?: string
  depth: AnalysisDepth
  sources: string[]
  /** Maximal 4000 Zeichen. */
  remarks?: string
  dataroom_url?: string
  profile_scope?: AnalysisProfileScope | null
}

/** Zustand eines einzelnen Agenten-Schritts (api#1191). */
export type AnalysisStepStatus = 'pending' | 'running' | 'done' | 'warn' | 'failed' | 'skipped'

/** Kurzergebnis eines Schritts als Schlüssel/Wert-Zeile. */
export interface AnalysisStepFact {
  k: string
  v: string
}

/** Ein Schritt des Agenten — eine Quelle oder ein Arbeitsgang (api#1191). */
export interface AnalysisStep {
  key: string
  status: AnalysisStepStatus
  summary: string | null
  facts: AnalysisStepFact[]
  finished_at: string | null
}

/** Ampel der fertigen Analyse (api#1192). */
export type AnalysisVerdict = 'go' | 'go_with_reservations' | 'stop'

/** Abgleich mit dem Suchprofil — `unknown`, wenn kein Profil hinterlegt war. */
export type AnalysisProfileMatch = 'fits' | 'partly' | 'no' | 'unknown'

/** Das beim Start festgehaltene Suchprofil (Snapshot). */
export interface AnalysisProfileRef {
  scope: AnalysisProfileScope
  entity_id: number
  label: string
  as_of: string | null
}

export interface Analysis {
  id: number
  /** Bestellende Firma — die Admin-Übersicht zeigt und filtert darauf (#1247). */
  company_id: number
  /**
   * Objekt und Adresse (api#1189). Optional, weil Analysen aus der Zeit vor
   * api#1189 diese Felder nicht haben.
   */
  title?: string
  street_address?: string | null
  zip_code?: string | null
  city?: string | null
  egrid_number?: string | null
  status: AnalysisStatus
  depth: AnalysisDepth
  sources: string[]
  price_chf: string
  property_id: number | null
  job_id: number | null
  report_document_id: number | null
  error: string | null
  created_at: string
  finished_at: string | null
  progress: number
  progress_message: string | null

  /**
   * Felder der Detailansicht (#1252). Optional, weil die Liste
   * (`GET /api/analyses`) sie nicht mitliefert und Analysen aus der Zeit vor
   * api#1190–1193 sie nicht haben — `GET /api/analyses/{id}` setzt sie.
   */
  requested_by?: { id: number; name: string } | null
  /** Hat der angemeldete Käufer selbst bestellt (api#1190)? */
  is_mine?: boolean
  steps?: AnalysisStep[]
  verdict?: AnalysisVerdict | null
  summary?: string | null
  open_points?: string[]
  /** Bemerkungen des Käufers aus der Bestellung. */
  remarks?: string | null
  profile?: AnalysisProfileRef | null
  profile_match?: AnalysisProfileMatch | null
  profile_match_reasons?: string[]
}

/** Eine Datei des Datenraums zu einer Analyse. */
export interface AnalysisDocument {
  id: number
  original_filename: string
  category: string | null
  mime_type: string | null
  file_size: number | null
}

export interface AnalysisDocumentsResponse {
  items: AnalysisDocument[]
}

export interface AnalysisDocumentsUploadResponse {
  uploaded: number
  skipped: number
}

export interface AnalysisListResponse {
  items: Analysis[]
  total: number
  page: number
  page_size: number
}

/** Wessen Analysen die Historie zeigt (api#1190) — Standard `all`. */
export type AnalysisScope = 'mine' | 'others' | 'all'

/**
 * Suche nach bereits bestellten Analysen zum selben Objekt (api#1193). Alle
 * Felder sind optional; der Server verlangt mindestens eines.
 */
export interface AnalysisDuplicateQuery {
  title?: string
  street_address?: string
  zip_code?: string
  egrid_number?: string
}

/** Ein Treffer der Dubletten-Suche — `match` sagt, worüber er gefunden wurde. */
export interface AnalysisRelatedItem {
  analysis_id: number
  title: string
  city: string | null
  requested_by: { id: number; name: string } | null
  created_at: string
  status: AnalysisStatus
  verdict: 'go' | 'go_with_reservations' | 'stop' | null
  match: 'egrid' | 'address' | 'title'
}

export interface AnalysisRelatedResponse {
  matches: AnalysisRelatedItem[]
}

/**
 * Rumpf für den Versand an einen Schätzer (#1257). Nur `appraiser_id` ist
 * Pflicht; `to_email` übersteuert die Adresse aus der Firmen-Einstellung.
 */
export interface AnalysisDispatchRequest {
  appraiser_id: number
  /** Höchstens 2000 Zeichen. */
  message?: string
  to_email?: string
}

/** Ein Eintrag des Versandprotokolls — Referenz und Kundennummer wie gesendet. */
export interface AnalysisDispatchItem {
  id: number
  analysis_id: number
  /** Leer, wenn der Schätzer inzwischen aus dem Verzeichnis gelöscht wurde. */
  appraiser_id: number | null
  /** Ebenso leer bei gelöschtem Schätzer — die Zeile bleibt im Protokoll. */
  appraiser_name: string | null
  to_email: string
  /** Adresse der Kopie (der Absender). */
  cc_email: string | null
  reference: string | null
  customer_number: string | null
  message: string | null
  status: 'sent' | 'failed'
  /** Grund des gescheiterten Versands — nur bei `status === 'failed'`. */
  error: string | null
  sent_at: string
  sent_by: { id: number; name: string } | null
}

export interface AnalysisDispatchListResponse {
  items: AnalysisDispatchItem[]
  total: number
}

export const analysesApi = {
  getSources: () => api.get<AnalysisSourcesResponse>('/api/analyses/sources'),

  create: (data: AnalysisCreate) => api.post<Analysis>('/api/analyses', data),

  /**
   * Eigene Analysen, neueste zuerst (#1185). Admins sehen alle Firmen und
   * schränken mit `company_id` auf eine einzelne Firma ein (#1247).
   * Die Historie des Käufers (#1253) filtert zusätzlich nach Besteller
   * (`scope`, Standard `all`), Freitext (`q`, maximal 100 Zeichen) und Ampel.
   */
  list: (params?: {
    page?: number
    page_size?: number
    company_id?: number
    scope?: AnalysisScope
    q?: string
    verdict?: AnalysisVerdict
  }) => api.get<AnalysisListResponse>('/api/analyses', { params }),

  get: (id: number) => api.get<Analysis>(`/api/analyses/${id}`),

  /**
   * Analysen derselben Firma zum gleichen Objekt (api#1193) — maximal fünf,
   * jüngste zuerst. Grundlage des Dubletten-Hinweises beim Bestellen (#1251).
   */
  duplicates: (data: AnalysisDuplicateQuery) =>
    api.post<AnalysisRelatedResponse>('/api/analyses/duplicates', data),

  /** Dateien des Datenraums dieser Analyse (#1252). */
  documents: (id: number) =>
    api.get<AnalysisDocumentsResponse>(`/api/analyses/${id}/documents`),

  /**
   * Frühere Analysen derselben Firma zum gleichen Objekt (api#1193) — die
   * Detailseite zeigt sie unter dem Ergebnis (#1252).
   */
  related: (id: number) => api.get<AnalysisRelatedResponse>(`/api/analyses/${id}/related`),

  /** Bericht als PDF; 404 mit `detail.code = 'NO_REPORT'`, solange keiner vorliegt (#1186). */
  getReportPdf: (id: number) =>
    api.get<Blob>(`/api/analyses/${id}/report`, { responseType: 'blob' }),

  /** Datenraum-Dateien nachreichen; ZIP wird serverseitig entpackt. */
  uploadDocuments: (id: number, files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    return api.post<AnalysisDocumentsUploadResponse>(`/api/analyses/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 min — 250 MB je Datei bei langsamer Verbindung
    })
  },

  /**
   * Preis dem Guthaben der Firma zurückbuchen (nur admin, #1247).
   * 409 mit `detail.code = 'REFUND_NOT_POSSIBLE'`, wenn die Analyse noch
   * offen ist oder bereits zurückgebucht wurde.
   */
  refund: (id: number) => api.post<Analysis>(`/api/analyses/${id}/refund`),

  /**
   * Bericht und Datenraum-Link an einen akkreditierten Schätzer senden (#1257).
   * 404 bei fremdem Auftrag oder unbekanntem/inaktivem Schätzer; 409 `NO_REPORT`,
   * solange kein Bericht vorliegt; 422 `NO_APPRAISER_EMAIL` ohne Empfängeradresse;
   * 502 `REPORT_UNAVAILABLE` (Dokumentenarchiv stumm) und 502 `MAIL_FAILED`
   * (Mail ging nicht raus — die Protokollzeile steht dann auf `failed`).
   */
  sendToAppraiser: (id: number, data: AnalysisDispatchRequest) =>
    api.post<AnalysisDispatchItem>(`/api/analyses/${id}/send-to-appraiser`, data),

  /** Versandprotokoll dieser Analyse, jüngste zuerst (#1257). */
  dispatches: (id: number) =>
    api.get<AnalysisDispatchListResponse>(`/api/analyses/${id}/dispatches`),
}

// =============================================================================
// Schätzer-Verzeichnis (api#1196)
// =============================================================================

/** Käufer-Sicht: nur was zur Auswahl eines Schätzers nötig ist. */
export interface Appraiser {
  id: number
  name: string
  website: string | null
  accredited: boolean
}

/** Admin-Sicht mit Kontaktdaten, Notizen und Zustand. */
export interface AppraiserAdmin extends Appraiser {
  email: string | null
  phone: string | null
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

/** Rumpf für POST und PUT; beim Ändern sind alle Felder optional (`Partial`). */
export interface AppraiserInput {
  name: string
  website?: string
  email?: string
  phone?: string
  accredited?: boolean
  notes?: string
}

export interface AppraiserListResponse {
  items: Appraiser[]
  total: number
}

export interface AppraiserAdminListResponse {
  items: AppraiserAdmin[]
  total: number
}

/**
 * Angaben der eigenen Firma zu einem Schätzer (#1256) — damit der Versand der
 * Objektanalyse Ansprechperson, Kundennummer und Referenz tragen kann.
 */
export interface CompanyAppraiserSetting {
  contact_name: string | null
  contact_email: string | null
  customer_number: string | null
  reference: string | null
  notes: string | null
}

/** Schätzer in der Käufer-Sicht samt der eigenen Einstellung (oder `null`). */
export interface CompanyAppraiserItem extends Appraiser {
  setting: CompanyAppraiserSetting | null
}

export interface CompanyAppraiserListResponse {
  items: CompanyAppraiserItem[]
}

/** Rumpf für PUT — alle Felder optional, die API führt ein Dict. */
export interface CompanyAppraiserSettingInput {
  contact_name?: string
  contact_email?: string
  customer_number?: string
  reference?: string
  notes?: string
}

export const appraisersApi = {
  /** Leseliste für Käufer — nur aktive Schätzer. */
  list: () => api.get<AppraiserListResponse>('/api/appraisers'),

  adminList: (includeInactive: boolean) =>
    api.get<AppraiserAdminListResponse>('/api/admin/appraisers', {
      params: { include_inactive: includeInactive },
    }),

  create: (data: AppraiserInput) => api.post<AppraiserAdmin>('/api/admin/appraisers', data),

  update: (id: number, data: Partial<AppraiserInput>) =>
    api.put<AppraiserAdmin>(`/api/admin/appraisers/${id}`, data),

  /** Setzt `is_active = false`; der Eintrag bleibt für Altdaten erhalten. */
  deactivate: (id: number) => api.delete<void>(`/api/admin/appraisers/${id}`),

  /**
   * Aktive Schätzer samt der Einstellung der eigenen Firma; 409 `NO_COMPANY`.
   *
   * Die drei `…/me/appraisers`-Endpunkte bringt erst das API-Ticket «Firmen-
   * Einstellungen je Schätzer» mit; bis zu dessen Deploy antwortet Staging mit
   * 404. `AppraisersTab` fängt das ab und zeigt dann `list()` als Leseliste mit
   * Hinweis, statt den Reiter im Fehlerzustand zu lassen.
   */
  mySettings: () =>
    api.get<CompanyAppraiserListResponse>('/api/investor/me/appraisers'),

  updateMySetting: (appraiserId: number, data: CompanyAppraiserSettingInput) =>
    api.put<CompanyAppraiserSetting>(
      `/api/investor/me/appraisers/${appraiserId}`,
      data
    ),

  /** Löscht die Angaben der eigenen Firma zu diesem Schätzer. */
  removeMySetting: (appraiserId: number) =>
    api.delete<void>(`/api/investor/me/appraisers/${appraiserId}`),
}
